// ─── src/stores/auraCore.types.ts ─────────────────────────────────────────────
// AuraCore contract definitions.
// All Aura surfaces must implement these interfaces.
// These are immutable contracts, additions only, never breaking changes.

// ─── Primitives ───────────────────────────────────────────────────────────────

export type ChatUiState = 'closed' | 'open' | 'fullchat';
export type WorkspaceMode = 'chat' | 'discovery' | 'fullscreen';
export type IntentExtractionMode = 'ai' | 'keyword' | 'context';
export type PriceSignal = 'budget' | 'mid-range' | 'luxury' | 'ultra-luxury';
export type ListingType = 'venue' | 'vendor' | 'all';
export type VenueStyle = 'romantic' | 'rustic' | 'modern' | 'historic' | 'garden' | 'coastal';

// ─── Message ──────────────────────────────────────────────────────────────────

export interface AuraEditorialCard {
  article_id: string;
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
}

export interface AuraShowcaseCard {
  showcase_id: string;
  title: string;
  slug: string;
  badge_label: string;
  cover_image: string;
}

export interface AuraRealWeddingCard {
  feature_id: string;
  title: string;
  slug: string;
  cover_image: string;
  photographer_name: string;
}

export interface AuraMessage {
  id: number;
  from: 'user' | 'aura';
  text: string;
  images?: string[];
  timestamp?: number;
  follow_up_prompts?: string[];
  editorial_context?: AuraEditorialCard[];
  showcase_context?: AuraShowcaseCard[];
  real_wedding_context?: AuraRealWeddingCard[];
  // Session state attached by orchestrator, used for context carry-forward
  _intent?: Record<string, unknown>;
  _listingIds?: string[];
  _query_id?: string;      // orchestrator query_id, used for feedback attribution
  // Architecture/28: the active comparison, echoed back verbatim next turn.
  // The client is a courier, never an interpreter — no code reads inside it.
  _activeComparison?: unknown;
  // Shop surface: affiliate products with feed imagery, rendered in the rail as
  // ProductPreview cards. Distinct from recommendations (member listings).
  product_context?: Array<{ brand?: string; name?: string; price?: string; url?: string; image?: string | null }>;
  product_browse?: { label?: string; link?: string };
  // System note, renders as a compact gold divider, not a full bubble
  _systemNote?: boolean;
  _systemNoteText?: string;
  // Recommendation intro, when set, the mobile bottom sheet renders
  // "View profile" + "Save" actions beneath the bubble for this listing id.
  _recIntroFor?: string;
}

// ─── Intent ───────────────────────────────────────────────────────────────────

export interface AuraIntent {
  country: string | null;
  location: string | null;
  locationAliases: string[];
  category: string | null;
  type: ListingType;
  style: VenueStyle | null;
  capacity: number | null;
  features: string[];
  priceSignal: PriceSignal | null;
  summary: string;
}

export interface IntentMetrics {
  latency: number;
  confidence: number;
  fallbackUsed: boolean;
  inputType: 'text' | 'voice';
  extractionMode: IntentExtractionMode;
  abTestVariant?: 'v1' | 'v2';
  fallbackReason?: string;
}

// ─── Listing (card item shape) ────────────────────────────────────────────────

export interface AuraListing {
  id: string;
  slug: string;
  type: 'venue' | 'vendor';
  category: string;
  name: string;
  city: string;
  region: string;
  country: string;
  imgs: string[];
  online: boolean;
  verified: boolean;
  lwdScore: number | null;
  priceFrom: string | null;
  capacity: number | null;
  tag: string | null;
  rank?: number;
  specialties?: string[];
  styles?: string[];
  // Aura scoring reason, shown on active/shortlisted cards
  reason?: string | null;
  // Phase 3, confirmed wedding-intelligence capabilities matching the brief
  matchReasons?: string[];
}

// ─── Recommendations ──────────────────────────────────────────────────────────

export interface AuraRecommendations {
  items: AuraListing[];
  summary: string;
  intent?: AuraIntent;
}

// ─── Active context (page-level) ──────────────────────────────────────────────

export interface AuraActiveContext {
  country: string | null;
  region: string | null;
  page: string | null;
  listingId: string | null;
  categorySlug: string | null;
}

// ─── Session ──────────────────────────────────────────────────────────────────

export interface AuraSession {
  // Tab-scoped ID. Each browser tab generates its own. Never shared across tabs.
  id: string;
  startTime: number;
  lastActivityTime: number;
}

// ─── Global preferences (synced across tabs) ─────────────────────────────────

export interface AuraPreferences {
  theme: 'light' | 'dark';
  dismissedPrompts: string[];
  sidebarCollapsed: boolean;
  compactMode: boolean;
}

// ─── Analytics event ──────────────────────────────────────────────────────────

export interface AuraAnalyticsEvent {
  type: string;
  payload: Record<string, unknown>;
  sessionId: string;
  timestamp: number;
  device: 'mobile' | 'tablet' | 'desktop';
}

// ─── Full state shape ─────────────────────────────────────────────────────────

export interface AuraState {
  // Tab-scoped session (never synced cross-tab)
  session: AuraSession;

  // Conversation (tab-local, NOT mirrored to other tabs)
  messages: AuraMessage[];
  isTyping: boolean;
  // PROTOTYPE (project_aura_streaming_ux_improvement.md, Step 3, aura/streaming-prototype
  // branch): real orchestration-stage progress, populated only when VITE_AURA_STREAM_URL
  // is configured. null in production today — every render path must treat it as optional.
  streamStage: { stage: string; label: string; tMs: number } | null;
  // Partial, already-validated explanation text as it is revealed. null until the
  // writing stage starts streaming; cleared once the turn finalises.
  streamingText: string | null;

  // Page context (drives fallback recommendations)
  activeContext: AuraActiveContext;

  // UI state
  chatUiState: ChatUiState;
  chatDark: boolean;

  // Intent pipeline
  intent: AuraIntent | null;
  intentMetrics: IntentMetrics | null;

  // Recommendations
  recommendations: AuraRecommendations;
  recommendationsLoading: boolean;
  articleRecommendations: AuraListing[];
  articlesLoading: boolean;

  // Global preferences (synced across tabs via localStorage)
  preferences: AuraPreferences;

  // Telemetry event buffer (flushed to backend periodically)
  analyticsBuffer: AuraAnalyticsEvent[];

  // Conversation intelligence (per-session, cleared on history reset)
  queryId: string | null;
  conversationMode: string | null;
  consecutiveClarificationCount: number;

  // Venue-Conversation Integration
  // activeVenueId: the venue currently "in focus" in the Aura conversation
  activeVenueId: string | null;
  // auraShortlistedIds: IDs of venues pinned during this session (for bulk enquiry)
  auraShortlistedIds: string[];
  // auraShortlistedItems: full item objects (needed to render ShortlistBar + BulkEnquiryModal)
  auraShortlistedItems: AuraListing[];
  // enquiredVenueIds: venues that have already received an enquiry this session.
  // Buying-signal chips and the enquiry popup are suppressed for these IDs.
  // Chips re-activate automatically when a different venue becomes active.
  enquiredVenueIds: string[];

  // ── The Wedding entity (feature-flagged: VITE_WEDDING_ENTITY) ──────────────
  // The persistent Wedding this session is building, plus its team roster.
  // Null until Aura silently mints one (wedding confidence >= 60). Owned by
  // auth.uid() (anonymous or permanent). See project_wedding_entity.
  wedding: Record<string, unknown> | null;
  weddingTeam: Record<string, unknown>[];
  // entity_ids the couple dismissed ("Not for us"), persisted via candidate_rejected
  // events and rehydrated on resume so dismissals survive reload.
  weddingDismissedIds: string[];
  // Active Aura-suggested candidate per role, rehydrated from candidate_suggested
  // events so the gold candidate box survives reload (keyed by role).
  weddingCandidates: Record<string, { item: Record<string, unknown>; reasoning: Record<string, unknown> }>;
  // The entity the couple selected in the wedding (Entity Focus), drives the right
  // column to recompose into that entity's dossier. Null = show query knowledge.
  focusedEntity: Record<string, unknown> | null;
}

// ─── Actions ──────────────────────────────────────────────────────────────────

export interface AuraActions {
  // UI
  openMiniBar(): void;
  openWorkspace(): void;
  closeChat(): void;
  closeFull(): void;
  toggleTheme(): void;

  // Context
  setChatContext(ctx: AuraActiveContext): void;

  // Conversation
  sendMessage(text: string): void;
  clearHistory(): void;

  // Intent (called internally by sendMessage; exposed for testing)
  setIntent(intent: AuraIntent, metrics?: IntentMetrics): void;

  // Recommendations
  setRecommendations(recs: AuraRecommendations): void;
  setRecommendationsLoading(loading: boolean): void;
  setArticleRecommendations(articles: AuraListing[]): void;

  // Preferences (global, cross-tab)
  setPreference<K extends keyof AuraPreferences>(key: K, value: AuraPreferences[K]): void;
  dismissPrompt(id: string): void;

  // Telemetry
  trackEvent(type: string, payload: Record<string, unknown>): void;
  flushAnalytics(): AuraAnalyticsEvent[];

  // Venue-Conversation Integration
  setActiveVenueId(id: string | null): void;
  // Mobile card tap, focus a recommendation and inject a concise Aura intro
  // (deterministic, no LLM call). Idempotent against repeated/delayed taps.
  focusRecommendation(id: string): void;
  auraShortlistVenue(item: AuraListing): void;
  auraShortlistRemove(id: string): void;
  clearAuraShortlist(): void;
  // Mark a venue as enquired, suppresses buying chips + popup for that ID
  markVenueEnquired(id: string): void;

  // The Wedding entity
  hydrateWedding(): Promise<void>;
  _syncWeddingFromTurn(intent: Record<string, unknown> | undefined, recommendations: AuraRecommendations | null, edgeTrace?: unknown[], reasoningId?: string): Promise<void>;
  _advanceTeamRole(item: AuraListing | Record<string, unknown>, state: string): Promise<void>;
  rejectCandidate(item: AuraListing | Record<string, unknown>): Promise<void>;
  focusEntity(entity: Record<string, unknown>): void;
  clearEntityFocus(): void;
  getTeamEvidence(entityId: string): Promise<{ reasons: Record<string, unknown>[]; timeline: { state: string | null; at: string }[] }>;
  logRecommendationReasoning(reasoning: Record<string, unknown>): Promise<void>;
  saveAndStartNewChat(): Promise<void>;
}

// ─── Hook return type ─────────────────────────────────────────────────────────

export type UseAuraReturn = AuraState & AuraActions;

// ─── Default values (shared between store and mock) ───────────────────────────

export const AURA_INIT_MESSAGE: AuraMessage = {
  id: 0,
  from: 'aura',
  text: "Hello, I'm Aura, your private wedding concierge. Tell me what you're looking for and I'll curate the perfect options.",
  timestamp: Date.now(),
};

export const AURA_DEFAULT_PREFERENCES: AuraPreferences = {
  theme: 'dark',
  dismissedPrompts: [],
  sidebarCollapsed: false,
  compactMode: false,
};

export const AURA_DEFAULT_RECOMMENDATIONS: AuraRecommendations = {
  items: [],
  summary: 'Curated for you',
};

export const AURA_DEFAULT_CONTEXT: AuraActiveContext = {
  country: null,
  region: null,
  page: null,
  listingId: null,
  categorySlug: null,
};
